import { Platform } from 'react-native';

/**
 * Lazy-loaded `expo-haptics` wrapper. The module is imported via
 * `require` so the web bundle doesn't pull native-only code into its
 * graph — `Platform.OS === 'web'` short-circuits before the require
 * runs.
 */

type HapticsModule = {
  impactAsync: (style: number) => Promise<void>;
  selectionAsync: () => Promise<void>;
  notificationAsync: (type: number) => Promise<void>;
  ImpactFeedbackStyle: { Light: number; Medium: number; Heavy: number };
  NotificationFeedbackType: { Success: number; Warning: number; Error: number };
};

let cached: HapticsModule | null | undefined;

function getHapticsModule(): HapticsModule | null {
  if (cached !== undefined) return cached;
  if (Platform.OS === 'web') {
    cached = null;
    return null;
  }
  try {
    // Lazy require keeps the web bundler from following this branch.
     
    cached = require('expo-haptics') as HapticsModule;
  } catch {
    cached = null;
  }
  return cached;
}

export type HapticStrength = 'light' | 'medium' | 'heavy';
export type HapticNotification = 'success' | 'warning' | 'error';

export interface HapticsApi {
  /** Confirms a primary tap (Button primary/success/warning/danger). */
  light: () => void;
  /** Confirms a commit — swipe-to-delete, toggle, drag-drop. */
  medium: () => void;
  /** Discouraging — destructive about-to-fire. */
  heavy: () => void;
  /** Value-change in a picker / segmented control / selectable card. */
  selection: () => void;
  /** Task completed. */
  success: () => void;
  /** Irreversible action ahead (ConfirmDialog danger). */
  warning: () => void;
  /** Operation failed. */
  error: () => void;
}

/**
 * Low-level haptics API. Most callers should use `useHaptics()` from
 * `libs/ui/src/hooks/useHaptics.ts` so the user preference + system
 * flag are honoured. Direct use here skips both gates — reserved for
 * library code that wants the raw signal (e.g. internal swipe-to-delete
 * primitive).
 */
function fire(strength: HapticStrength): void {
  const mod = getHapticsModule();
  if (!mod) return;
  const styleMap = {
    light: mod.ImpactFeedbackStyle.Light,
    medium: mod.ImpactFeedbackStyle.Medium,
    heavy: mod.ImpactFeedbackStyle.Heavy,
  };
  void mod.impactAsync(styleMap[strength]);
}

function fireNotification(type: HapticNotification): void {
  const mod = getHapticsModule();
  if (!mod) return;
  const typeMap = {
    success: mod.NotificationFeedbackType.Success,
    warning: mod.NotificationFeedbackType.Warning,
    error: mod.NotificationFeedbackType.Error,
  };
  void mod.notificationAsync(typeMap[type]);
}

function fireSelection(): void {
  const mod = getHapticsModule();
  if (!mod) return;
  void mod.selectionAsync();
}

/**
 * Direct haptics — bypasses the user-preference layer. Library code
 * should prefer `useHaptics()` so the user can mute feedback globally.
 */
export const hapticsRaw: HapticsApi = {
  light: () => fire('light'),
  medium: () => fire('medium'),
  heavy: () => fire('heavy'),
  selection: () => fireSelection(),
  success: () => fireNotification('success'),
  warning: () => fireNotification('warning'),
  error: () => fireNotification('error'),
};
